import React, { useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import {
  Bar, CartesianGrid, ComposedChart, LabelList, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import {
  getExperienceSummary, getExperienceTrend, getExperienceDetail,
} from "../../services/ops/opsservices";
import { useClientTimeZone, zoneAbbr } from "../../utils/clienttimezone";
import ConversationDialog from "./ConversationDialog";
import {
  BLUE, GREEN, AMBER, RED, GRAY, NAVY,
  CounterCard, ChartCard, SectionTitle, Caption, DetailTable, LoadingRow,
  OpsSlicers, StatusChip, identityColumns, drillColumn, useOpsData,
  DEFAULT_GRAN, DEFAULT_RANGE, getDateRange,
  formatRangeLabel, isSubDayGran,
  fmtInt, fmtPct, fmtDate, fmtAxisDate, fmtTime, fmtDuration, safeNum,
} from "./opsShared";

// Repeat messages are noise below this and a symptom above it. Derived from
// the healthy baseline: with replies landing in ~7 seconds, almost nobody has
// time to text again before being answered.
const REPEAT_RATE_WARN = 2;
const REPEAT_RATE_BAD  = 5;

export default function CustomerExperienceDashboard() {
  const [gran,  setGran]  = useState(DEFAULT_GRAN);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { timeZone, tzReady } = useClientTimeZone();
  const tzLabel = zoneAbbr(timeZone);

  const params = useMemo(() => ({
    ...getDateRange(range, timeZone),
    granularity: gran.toLowerCase(),
  }), [range, gran, timeZone]);

  // Any account row opens its full conversation.
  const [drill, setDrill] = useState(null);
  const openThread = (row) => setDrill(row);

  const { data, loading, error } = useOpsData({
    summary: getExperienceSummary,
    trend:   getExperienceTrend,
    detail:  getExperienceDetail,
  }, params, tzReady);

  const s = data.summary || {};
  const detail = data.detail || [];

  const messages = safeNum(s.messagesReceived);
  const repeats  = safeNum(s.repeatMessages);
  const repeatRate = messages > 0 ? (repeats / messages) * 100 : 0;
  const repeatStatus =
    repeatRate >= REPEAT_RATE_BAD ? "bad" : repeatRate >= REPEAT_RATE_WARN ? "warn" : "ok";

  // ── TWO POPULATIONS, PREVIOUSLY ONE ──
  // "Texted again before the reply arrived" was almost entirely people still
  // TYPING — Vera answers in ~7s, so any second message inside that window was
  // flagged automatically, and the count went UP as the agent got faster.
  //
  // Server-side split now: under 60s is composition, over it with no answer is
  // somebody who waited through a full cycle and got nothing.
  const composed     = safeNum(s.composedMessages);
  const stillWaiting = safeNum(s.gaveUpWaiting);

  const trend = (data.trend || []).map((r) => ({
    date:      fmtAxisDate(r.summary_date, gran, timeZone),
    messages:  safeNum(r.messagesReceived),
    active:    safeNum(r.conversationsActive),
    repeats:   safeNum(r.repeatMessages),
    errors:    safeNum(r.endedByError),
    rate:      safeNum(r.repeatRate),
  }));

  const rangeLabel = formatRangeLabel(params, timeZone);
  const titleOf = (t) => `${t} · ${rangeLabel}`;

  const detailColumns = [
    drillColumn(openThread),
    ...identityColumns,
    { key: "firstMessage",  label: "First Message",  width: 190 },
    { key: "repeatMessage", label: "Texted Again",   width: 190 },
    { key: "secondsBetween", label: "Gap", width: 80, align: "right",
      render: (r) => fmtDuration(r.secondsBetween) },
    { key: "repeatReason", label: "Why", width: 130, align: "center",
      render: (r) => (
        r.repeatReason === "Gave up waiting"
          ? <StatusChip label="Gave up waiting" tone="bad" />
          : r.repeatReason === "Still typing"
            ? <StatusChip label="Still typing" tone="default" />
            : <StatusChip label="New thought" tone="default" />
      ) },
    { key: "repeatedAtUtc", label: "When", width: 160,
      render: (r) => fmtTime(r.repeatedAtUtc, timeZone) },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <OpsSlicers gran={gran} setGran={setGran} range={range} setRange={setRange} tzLabel={tzLabel} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LoadingRow text="Reading customer signals…" />}

      {!loading && (
        <>
          <SectionTitle>Customer Experience</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <CounterCard label="Messages Received" value={fmtInt(messages)} color={NAVY} />
            <CounterCard label="Active Conversations" value={fmtInt(s.conversationsActive)} color={BLUE} />
            <CounterCard label="Repeat Messages" value={fmtInt(repeats)}
              color={GREEN} status={repeatStatus}
              sub="Texted again within 2 min" />
            <CounterCard label="Repeat Rate" value={fmtPct(repeatRate)}
              color={GREEN} status={repeatStatus} />
            <CounterCard label="Still Typing" value={fmtInt(composed)}
              color={GRAY}
              sub="Second text within 60s — normal" />
            <CounterCard label="Gave Up Waiting" value={fmtInt(stillWaiting)}
              color={GREEN} status={stillWaiting === 0 ? "ok" : "bad"}
              sub="Waited over 60s, no reply, texted again" />
          </Box>

          {stillWaiting > 0 && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>
              {stillWaiting}{" "}
              {stillWaiting === 1 ? "customer waited" : "customers waited"} more than a minute
              with no reply and texted again. This is the earliest signal that Vera is not
              keeping up — it moves before response times do, because customers react faster
              than the averages. Messages sent within 60 seconds of each other are counted
              separately as "Still typing"; those are people composing across two texts, not
              waiting.
            </Alert>
          )}

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Repeat messages")} height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right"
                    tick={{ fontSize: 10, fill: AMBER }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  {/* Volume behind, so a spike in repeats can be read against
                      how many messages it came out of. */}
                  <Bar yAxisId="left" dataKey="messages" name="Messages received"
                    fill={`${NAVY}22`} radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="left" dataKey="repeats" name="Repeat messages"
                    fill={AMBER} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="repeats" position="top" fontSize={10} fill={AMBER}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                  <Line yAxisId="right" type="monotone" dataKey="rate" name="Repeat rate"
                    stroke={AMBER} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
            <Caption>
              Customers text again when nothing comes back. This climbed through August
              alongside the response failures and should sit near zero when Vera is
              answering in seconds.
            </Caption>
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <ChartCard title={titleOf("Conversations reaching a customer vs ending in error")} height={240}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: "#888" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="active" name="Active conversations" fill={BLUE} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="errors" name="Ended in error" fill={RED} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="errors" position="top" fontSize={10} fill={RED}
                      formatter={(v) => (v > 0 ? v : "")} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Box>

          <SectionTitle right={`${detail.length} shown`}>
            Customers Who Texted Again
          </SectionTitle>
          <DetailTable
            columns={detailColumns}
            rows={detail}
            emptyText="Nobody had to text twice — Vera answered everyone first time."
            maxHeight={440}
          />
          <Caption>
            "Why" is what separates a customer still composing from one who gave up. Most
            rows are Still typing — a typo correction, adding the YES keyword, finishing a
            sentence — and those are normal SMS behaviour rather than a fault. Only "Gave up
            waiting" is worth reading.
          </Caption>
        </>
      )}

      <ConversationDialog
        open={!!drill}
        onClose={() => setDrill(null)}
        customerdataid={drill?.customerdataid}
      />
    </Box>
  );
}